import { BaseComponent } from './BaseComponent';

export interface FerzUIConfig {
  prefix: string;
  animationDuration: number;
  reducedMotion: boolean;
}

export type ComponentConstructor = new (element: HTMLElement, options?: any) => BaseComponent;

export class FerzUI {
  private components: Map<string, ComponentConstructor> = new Map();
  private eventListeners: Map<string, Array<{ callback: Function, options: any }>> = new Map();
  private instances: Map<string, BaseComponent> = new Map();
  public config: FerzUIConfig;
  private resizeObserver?: ResizeObserver;
  private intersectionObserver?: IntersectionObserver;

  constructor() {
    this.config = {
      prefix: 'fz',
      animationDuration: 200,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
    };
    
    this.init();
  }
  
  private init(): void {
    this.bindEvents();
    this.initializeComponents();
  }
  
  private bindEvents(): void {
    document.addEventListener('click', this.handleGlobalClick.bind(this));
    document.addEventListener('keydown', this.handleGlobalKeydown.bind(this));
    document.addEventListener('focusin', this.handleGlobalFocus.bind(this));
    document.addEventListener('focusout', this.handleGlobalFocus.bind(this));
    
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));
    }
    
    if (window.IntersectionObserver) {
      this.intersectionObserver = new IntersectionObserver(
        this.handleIntersection.bind(this),
        { rootMargin: '50px' }
      );
    }
  }
  
  public registerComponent(name: string, ComponentClass: ComponentConstructor): void {
    this.components.set(name, ComponentClass);
  }
  
  public getComponent(name: string): ComponentConstructor | undefined {
    return this.components.get(name);
  }
  
  public createInstance<T extends BaseComponent>(componentName: string, element: HTMLElement, options: any = {}): T | null {
    const ComponentClass = this.getComponent(componentName);
    if (!ComponentClass) {
      console.warn(`Component "${componentName}" not found`);
      return null;
    }
    
    const instance = new ComponentClass(element, options) as T;
    const id = this.generateId();
    this.instances.set(id, instance);
    element.setAttribute(`data-${this.config.prefix}-instance`, id);
    
    return instance;
  }
  
  public getInstance<T extends BaseComponent>(element: HTMLElement): T | null {
    const id = element.getAttribute(`data-${this.config.prefix}-instance`);
    return id ? (this.instances.get(id) as T) : null;
  }
  
  public destroyInstance(element: HTMLElement): void {
    const instance = this.getInstance(element);
    if (instance) {
      instance.destroy();
      const id = element.getAttribute(`data-${this.config.prefix}-instance`);
      if (id) this.instances.delete(id);
      element.removeAttribute(`data-${this.config.prefix}-instance`);
    }
  }
  
  public on(event: string, callback: Function, options: any = {}): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)?.push({ callback, options });
  }
  
  public off(event: string, callback: Function): void {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners?.findIndex(listener => listener.callback === callback) ?? -1;
      if (index > -1) {
        listeners?.splice(index, 1);
      }
    }
  }
  
  public emit(event: string, detail: any = {}): void {
    const customEvent = new CustomEvent(`fz:${event}`, {
      detail,
      bubbles: true,
      cancelable: true
    });
    document.dispatchEvent(customEvent);
    
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(({ callback, options }) => {
        try {
          callback(detail, options);
        } catch (error) {
          console.error(`Error in event listener for "${event}":`, error);
        }
      });
    }
  }
  
  private generateId(): string {
    return `fz-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  
  public debounce(func: Function, wait: number): Function {
    let timeout: any;
    return (...args: any[]) => {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  public throttle(func: Function, limit: number): Function {
    let inThrottle: boolean;
    return (...args: any[]) => {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  private handleGlobalClick(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest('[data-fz-component]') as HTMLElement;
    if (target) {
      const componentName = target.getAttribute('data-fz-component');
      if (componentName) {
        const instance = this.getInstance(target) || this.createInstance(componentName, target);
        if (instance && (instance as any).handleClick) {
          (instance as any).handleClick(event);
        }
      }
    }
  }
  
  private handleGlobalKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.emit('escape');
    }
    
    if (event.key === 'Enter' || event.key === ' ') {
      const target = (event.target as HTMLElement).closest('[data-fz-component]') as HTMLElement;
      if (target) {
        const instance = this.getInstance(target);
        if (instance && (instance as any).handleKeydown) {
          (instance as any).handleKeydown(event);
        }
      }
    }
  }
  
  private handleGlobalFocus(event: FocusEvent): void {
    const target = (event.target as HTMLElement).closest('[data-fz-component]') as HTMLElement;
    if (target) {
      const instance = this.getInstance(target);
      if (instance && (instance as any).handleFocus) {
        (instance as any).handleFocus(event);
      }
    }
  }
  
  private handleResize(entries: ResizeObserverEntry[]): void {
    entries.forEach(entry => {
      const element = entry.target as HTMLElement;
      const instance = this.getInstance(element);
      if (instance && (instance as any).handleResize) {
        (instance as any).handleResize(entry);
      }
    });
  }
  
  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      const element = entry.target as HTMLElement;
      const instance = this.getInstance(element);
      if (instance && (instance as any).handleIntersection) {
        (instance as any).handleIntersection(entry);
      }
    });
  }
  
  public initializeComponents(container: HTMLElement | Document = document): void {
    const componentElements = container.querySelectorAll('[data-fz-component]');
    componentElements.forEach(element => {
      const el = element as HTMLElement;
      const componentName = el.getAttribute('data-fz-component');
      if (componentName && !this.getInstance(el)) {
        this.createInstance(componentName, el);
      }
    });
  }
  
  public destroy(): void {
    this.instances.forEach(instance => {
      instance.destroy();
    });
    this.instances.clear();
    
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.eventListeners.clear();
  }
}
